import Vue from 'vue';
import Vuetify from 'vuetify/lib/framework';

Vue.use(Vuetify);

// Leggi le impostazioni salvate dal localStorage all'avvio
const SETTINGS_KEY = 'elefante_settings';
const savedSettings = localStorage.getItem(SETTINGS_KEY);
const parsedSettings = savedSettings ? JSON.parse(savedSettings) : {};
const savedPalette = parsedSettings.colorPalette || 'blueOcean'; // Default: Blu Oceano

// Palette di colori disponibili
const colorPalettes = {
  default: {
    primary: '#4285F4',
    secondary: '#424242',
    accent: '#9BBDF9',
    error: '#FF5252',
    info: '#64B5F6',
    success: '#66BB6A',
    warning: '#FFC107',
    if65Blue: '#5C8ED6',
    if65Silver: '#B0BEC5',
    if65Red: '#EF5350',
    if65Green: '#66BB6A',
    if65Yellow: '#FFCA28',
  },
  blueOcean: {
    primary: '#2C3E50',
    secondary: '#34495E',
    accent: '#3498DB',
    error: '#E74C3C',
    info: '#1ABC9C',
    success: '#2ECC71',
    warning: '#F39C12',
    if65Blue: '#3498DB',
    if65Silver: '#BDC3C7',
    if65Red: '#E74C3C',
    if65Green: '#2ECC71',
    if65Yellow: '#F1C40F',
  },
  greenNature: {
    primary: '#27AE60',
    secondary: '#2C3E50',
    accent: '#16A085',
    error: '#C0392B',
    info: '#3498DB',
    success: '#2ECC71',
    warning: '#F39C12',
    if65Blue: '#3498DB',
    if65Silver: '#BDC3C7',
    if65Red: '#C0392B',
    if65Green: '#27AE60',
    if65Yellow: '#F1C40F',
  },
  purpleElegance: {
    primary: '#8E44AD',
    secondary: '#2C3E50',
    accent: '#9B59B6',
    error: '#E74C3C',
    info: '#3498DB',
    success: '#2ECC71',
    warning: '#F1C40F',
    if65Blue: '#3498DB',
    if65Silver: '#BDC3C7',
    if65Red: '#E74C3C',
    if65Green: '#2ECC71',
    if65Yellow: '#F1C40F',
  },
  redEnergy: {
    primary: '#C0392B',
    secondary: '#34495E',
    accent: '#E74C3C',
    error: '#D35400',
    info: '#3498DB',
    success: '#27AE60',
    warning: '#F39C12',
    if65Blue: '#3498DB',
    if65Silver: '#BDC3C7',
    if65Red: '#C0392B',
    if65Green: '#27AE60',
    if65Yellow: '#F39C12',
  },
  grayNeutral: {
    primary: '#607D8B',
    secondary: '#455A64',
    accent: '#78909C',
    error: '#F44336',
    info: '#2196F3',
    success: '#4CAF50',
    warning: '#FF9800',
    if65Blue: '#2196F3',
    if65Silver: '#B0BEC5',
    if65Red: '#F44336',
    if65Green: '#4CAF50',
    if65Yellow: '#FFC107',
  },
  earthTerra: {
    primary: '#CB6949',
    secondary: '#5D4037',
    accent: '#BCAAA4',
    error: '#D84315',
    info: '#795548',
    success: '#689F38',
    warning: '#F57C00',
    if65Blue: '#607D8B',
    if65Silver: '#A1887F',
    if65Red: '#D84315',
    if65Green: '#689F38',
    if65Yellow: '#F57C00',
  }
};

// Ottieni i colori dalla palette salvata o usa blueOcean come default
const initialColors = colorPalettes[savedPalette] || colorPalettes.blueOcean;

const vuetify = new Vuetify({
    defaultAssets: {
        font: false, // Disabilita Roboto
        icons: 'mdi', // Material Design Icons
    },
    theme: {
        options: {
            customProperties: true, // Abilita le variabili CSS
        },
        themes: {
            light: {
                primary: initialColors.primary,
                secondary: initialColors.secondary,
                accent: initialColors.accent,
                error: initialColors.error,
                info: initialColors.info,
                success: initialColors.success,
                warning: initialColors.warning,

                // Colori personalizzati
                if65Blue: initialColors.if65Blue,
                if65Silver: initialColors.if65Silver,
                if65Red: initialColors.if65Red,
                if65Green: initialColors.if65Green,
                if65Yellow: initialColors.if65Yellow,
                background: '#F5F7FA',   // Sfondo chiaro
                cardBackground: '#FFFFFF', // Sfondo carte
                textPrimary: '#263238',  // Testo principale
                textSecondary: '#546E7A', // Testo secondario
            }
        }
    },
    options: {
        customProperties: true
    },
    icons: {
        iconfont: 'mdi',
        values: {
            dashboard: 'mdi-view-dashboard',
            project: 'mdi-projector-screen-outline',
            exit: 'mdi-exit-to-app',
            team: 'mdi-account-group',
            task: 'mdi-clock-time-eight',
            activity: 'mdi-application-outline',
            product: 'mdi-application-cog-outline',
            sub_activity: 'mdi-application-parentheses-outline',
            backup: 'mdi-database-clock-outline',
        }
    },
    typography: {
        fontFamily: 'Montserrat, sans-serif',
    },
});

// Funzione per aggiornare i colori del tema
const updateThemeColors = (colors) => {
    // Aggiorna i colori del tema
    vuetify.framework.theme.themes.light.primary = colors.primary;
    vuetify.framework.theme.themes.light.secondary = colors.secondary;
    vuetify.framework.theme.themes.light.accent = colors.accent;
    vuetify.framework.theme.themes.light.error = colors.error;
    vuetify.framework.theme.themes.light.info = colors.info;
    vuetify.framework.theme.themes.light.success = colors.success;
    vuetify.framework.theme.themes.light.warning = colors.warning;
    vuetify.framework.theme.themes.light.if65Blue = colors.if65Blue;
    vuetify.framework.theme.themes.light.if65Silver = colors.if65Silver;
    vuetify.framework.theme.themes.light.if65Red = colors.if65Red;
    vuetify.framework.theme.themes.light.if65Green = colors.if65Green;
    vuetify.framework.theme.themes.light.if65Yellow = colors.if65Yellow;
};

// Funzione per aggiornare il font del tema
const updateThemeFont = (fontFamily) => {
    try {
        // Aggiorna direttamente la proprietà CSS
        document.documentElement.style.setProperty('--v-font-family', fontFamily);

        // Forza l'aggiornamento del font con un foglio di stile
        const styleId = 'vuetify-font-style';
        let styleEl = document.getElementById(styleId);

        if (!styleEl) {
            styleEl = document.createElement('style');
            styleEl.id = styleId;
            document.head.appendChild(styleEl);
        }

        styleEl.textContent = `
            .v-application { font-family: ${fontFamily} !important; }
            .v-application * { font-family: ${fontFamily} !important; }
            .v-application .headline,
            .v-application .title,
            .v-application .subtitle-1,
            .v-application .subtitle-2,
            .v-application .body-1,
            .v-application .body-2,
            .v-application .caption,
            .v-application .overline {
                font-family: ${fontFamily} !important;
            }
        `;

        // Aggiorna anche la tipografia se disponibile
        if (vuetify.framework && vuetify.framework.typography) {
            vuetify.framework.typography.fontFamily = fontFamily;
        }
    } catch (error) {
        console.error('Errore nell\'aggiornamento del font:', error);
    }
};

// Rendi disponibile l'istanza di Vuetify e le funzioni di aggiornamento a livello globale
window.$vuetify = vuetify;
window.$updateVuetifyTheme = updateThemeColors;
window.$updateVuetifyFont = updateThemeFont;

// Applica i colori salvati se disponibili
if (window.vuetifyColors) {
    updateThemeColors(window.vuetifyColors);
}

// Applica il font salvato se disponibile
if (window.vuetifyFont) {
    updateThemeFont(window.vuetifyFont);
}

export default vuetify;
